import { Component, Input } from '@angular/core';
import { SnackBarService } from 'src/app/core/services/snackBar.service';

/**
 * Campo de solo lectura para credenciales: muestra label + valor, con opción de
 * enmascarar (type password + ojito) cuando es secreto, y botón de copiar al
 * portapapeles. Usado en el tab "Empresas de transporte".
 */
@Component({
  selector: 'app-copyable-secret',
  template: `
    <div class="cs-field">
      <span class="cs-field__label">{{ label }}</span>
      <span class="cs-field__value" [class.cs-field__value--empty]="!value">
        {{ value ? ((secret && !revealed) ? mask : value) : '—' }}
      </span>
      <div class="cs-field__actions" *ngIf="value">
        <button *ngIf="secret" type="button" class="cs-icon-btn" (click)="revealed = !revealed"
          [matTooltip]="revealed ? 'Ocultar' : 'Ver'">
          <i class="fas" [class.fa-eye]="!revealed" [class.fa-eye-slash]="revealed"></i>
        </button>
        <button type="button" class="cs-icon-btn" (click)="copy()" matTooltip="Copiar">
          <i class="fas fa-copy"></i>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .cs-field {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.2rem 0;
    }
    .cs-field__label {
      flex: 0 0 110px;
      color: #98a2b3;
      font-size: 12.5px;
    }
    .cs-field__value {
      flex: 1 1 auto;
      color: #344054;
      font-size: 13px;
      word-break: break-all;
      font-family: monospace;
    }
    .cs-field__value--empty {
      font-family: inherit;
      color: #98a2b3;
    }
    .cs-field__actions {
      flex: 0 0 auto;
      display: flex;
      gap: 0.25rem;
    }
    .cs-icon-btn {
      border: none;
      background: transparent;
      cursor: pointer;
      padding: 0.25rem;
      color: #98a2b3;
      line-height: 1;
    }
    .cs-icon-btn:hover {
      color: #344054;
    }
    .cs-icon-btn:focus {
      outline: none;
    }
    .cs-icon-btn i {
      font-size: 13px;
    }
  `]
})
export class CopyableSecretComponent {
  @Input() label: string;
  @Input() value: string;
  @Input() secret = false;

  revealed = false;
  readonly mask = '••••••••••';

  constructor(private snackBarService: SnackBarService) { }

  copy(): void {
    if (!this.value) return;
    const onSuccess = () => this.snackBarService.openSnackBar('Copiado al portapapeles', undefined, 'success');
    const onError = () => this.snackBarService.openSnackBar('No se pudo copiar al portapapeles', undefined, 'error');

    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(this.value).then(onSuccess, () => this.fallbackCopy(onSuccess, onError));
    } else {
      this.fallbackCopy(onSuccess, onError);
    }
  }

  private fallbackCopy(onSuccess: () => void, onError: () => void): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = this.value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      ok ? onSuccess() : onError();
    } catch (error) {
      onError();
    }
  }
}
